import { useState } from "react";
import { ArrowRight, Check, Linkedin } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useLang } from "@/i18n/LanguageContext";
import { useSEO } from "@/hooks/useSEO";
import Reveal from "@/components/Reveal";

export default function Contact() {
  useSEO("contact");
  const { tr } = useLang();
  const c = tr("contact");
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    const errs = {};
    if (!form.name.trim() || form.name.trim().length < 2) errs.name = c.errName;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) errs.email = c.errEmail;
    if (!form.message.trim() || form.message.trim().length < 10) errs.message = c.errMessage;
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setBusy(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success(c.success);
    } catch (err) {
      toast.error(c.error);
      setErrors({ form: formatApiError(err) });
    } finally {
      setBusy(false);
    }
  };

  const inputCls =
    "w-full border border-ink/15 bg-white px-4 py-3.5 text-sm text-ink placeholder:text-ink-muted/50 transition-colors focus:border-gold focus:outline-none focus:ring-1 focus:ring-gold/40";
  const labelCls = "mb-2 block font-mono text-[11px] uppercase tracking-[0.2em] text-ink-muted";

  return (
    <section data-testid="contact-page" className="relative min-h-screen overflow-hidden bg-ivory px-6 pb-24 pt-32 lg:px-10 lg:pt-44">
      <div className="mx-auto grid max-w-7xl gap-16 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-ink-muted">
              <span className="h-px w-10 bg-gold" /> LB Trade Advisory
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-6 font-serif text-5xl tracking-tight text-ink lg:text-6xl" data-testid="contact-title">
              <em className="text-[#8a7448]">{c.title?.[0]}</em>
            </h1>
          </Reveal>
          <Reveal delay={0.18}>
            <p className="mt-6 max-w-md text-base leading-relaxed text-ink-muted sm:text-lg">{c.sub}</p>
          </Reveal>
          <div className="mt-12" data-testid="contact-steps">
            <Reveal delay={0.24}>
              <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-gold" style={{ color: "#8a7448" }}>
                {c.stepsTitle}
              </p>
            </Reveal>
            <div className="mt-6 space-y-6">
              {(c.steps || []).map((s, i) => (
                <Reveal key={s.t} delay={0.3 + i * 0.09}>
                  <div className="flex items-start gap-5 border-t border-ink/10 pt-5">
                    <span className="font-mono text-sm text-gold" style={{ color: "#8a7448" }}>
                      0{i + 1}
                    </span>
                    <div>
                      <h3 className="font-serif text-lg text-ink">{s.t}</h3>
                      <p className="mt-1 text-sm text-ink-muted">{s.d}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          <Reveal delay={0.4}>
            <a
              href="https://www.linkedin.com/company/lb-trade-advisory"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-linkedin"
              className="mt-10 inline-flex items-start gap-4 border border-ink/10 bg-white px-5 py-4 transition-colors hover:border-gold/60"
            >
              <span className="flex h-9 w-9 shrink-0 items-center justify-center bg-navy text-ivory">
                <Linkedin className="h-4 w-4" />
              </span>
              <span>
                <span className="block font-serif text-base text-ink">{c.followTitle}</span>
                <span className="mt-0.5 block text-sm text-ink-muted">{c.followText}</span>
              </span>
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <Reveal delay={0.15}>
            {sent ? (
              <div data-testid="contact-success" className="flex h-full min-h-[420px] flex-col items-center justify-center border border-gold/40 bg-white p-12 text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-gold/15 text-[#8a7448]">
                  <Check className="h-7 w-7" />
                </span>
                <h2 className="mt-8 font-serif text-2xl text-ink sm:text-3xl">{c.success}</h2>
                <p className="mt-4 max-w-sm text-sm text-ink-muted">{c.successNote}</p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                noValidate
                data-testid="contact-form"
                className="border border-ink/10 bg-white p-8 shadow-[0_30px_80px_-40px_rgba(7,21,34,0.25)] lg:p-12"
              >
                <div className="grid gap-6 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className={labelCls}>{c.name} *</label>
                    <input
                      id="contact-name"
                      data-testid="contact-name-input"
                      className={inputCls}
                      value={form.name}
                      onChange={upd("name")}
                      placeholder={c.namePh}
                    />
                    {errors.name && <p className="mt-2 text-xs text-red-700">{errors.name}</p>}
                  </div>
                  <div>
                    <label htmlFor="contact-email" className={labelCls}>{c.email} *</label>
                    <input
                      id="contact-email"
                      data-testid="contact-email-input"
                      type="email"
                      className={inputCls}
                      value={form.email}
                      onChange={upd("email")}
                      placeholder={c.emailPh}
                    />
                    {errors.email && <p className="mt-2 text-xs text-red-700">{errors.email}</p>}
                  </div>
                </div>
                <div className="mt-6">
                  <label htmlFor="contact-company" className={labelCls}>{c.company}</label>
                  <input
                    id="contact-company"
                    data-testid="contact-company-input"
                    className={inputCls}
                    value={form.company}
                    onChange={upd("company")}
                    placeholder={c.companyPh}
                  />
                </div>
                <div className="mt-6">
                  <label htmlFor="contact-message" className={labelCls}>{c.message} *</label>
                  <textarea
                    id="contact-message"
                    data-testid="contact-message-input"
                    rows={6}
                    className={`${inputCls} resize-y`}
                    value={form.message}
                    onChange={upd("message")}
                    placeholder={c.messagePh}
                  />
                  {errors.message && <p className="mt-2 text-xs text-red-700">{errors.message}</p>}
                </div>
                {errors.form && <p className="mt-4 text-sm text-red-700" data-testid="contact-form-error">{errors.form}</p>}
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={busy}
                  className="group mt-8 inline-flex w-full items-center justify-center gap-3 bg-navy px-8 py-4 text-xs font-semibold uppercase tracking-[0.2em] text-ivory transition-colors hover:bg-navy-light disabled:pointer-events-none disabled:opacity-50 sm:w-auto"
                >
                  {busy ? c.sending : c.send}
                  {!busy && <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />}
                </button>
              </form>
            )}
          </Reveal>
        </div>
      </div>
    </section>
  );
}
